import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faFileCsv,
  faMagnifyingGlass,
  faSort,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  COLUMN_GROUPS,
  buildColumns,
  buildHeaderGroups,
  downloadCsv,
  filterTree,
  formatDelta,
  formatDeltaPct,
  formatExact,
  formatValue,
  readCell,
  sortTree,
  toCsv,
  totalIsAvailable,
} from "../columns";
const NAME_WIDTH = 340;

const DENSITY = { cell: "px-2 py-1 text-xs", name: "px-2 py-0.5 text-xs" };

/** Higher is better for every metric here, so a positive variance is favourable.
 *  Colour reinforces the +/- sign rather than replacing it, which keeps the table
 *  readable in greyscale and for colour-blind users. */
function deltaTone(value) {
  if (value === null || value === undefined) return "text-gray-300";
  if (value > 0) return "text-emerald-600";
  if (value < 0) return "text-red-600";
  return "text-gray-400";
}

/** Light tint on percentage variances only — enough to scan a wide grid for outliers
 *  without turning it into a heat map. */
function deltaTint(value) {
  if (!hasFigure(value)) return "";
  const magnitude = Math.abs(Number(value));
  if (magnitude < MATERIAL_THRESHOLD) return "";
  if (value > 0) return magnitude >= ALERT_THRESHOLD ? "bg-emerald-100/70" : "bg-emerald-50";
  return magnitude >= ALERT_THRESHOLD ? "bg-red-100/70" : "bg-red-50";
}

const MATERIAL_THRESHOLD = 5;
const ALERT_THRESHOLD = 5;

const hasFigure = (value) =>
  value !== null && value !== undefined && !Number.isNaN(Number(value));

const isImmaterial = (value) =>
  hasFigure(value) && Math.abs(Number(value)) <= MATERIAL_THRESHOLD;

const isAlert = (value) => hasFigure(value) && Number(value) <= -ALERT_THRESHOLD;

const SECTION_DIVIDER = "border-l border-[#c9d6e6]";
const SECTION_TINTS = ["bg-[#f4f8fc]", "bg-[#e7eff8]"];

function Cell({ row, column, density, isSectionStart }) {
  const value = readCell(row, column);
  const divider = isSectionStart ? SECTION_DIVIDER : "";

  if (column.kind === "value") {
    const tone = column.tone === "reference" ? "font-medium text-gray-400" : "text-gray-700";
    return (
      <td
        className={`${density.cell} whitespace-nowrap border-b border-gray-100 text-right tabular-nums ${tone} ${divider}`}
        title={formatExact(value, column.unit)}
      >
        {formatValue(value, column.unit)}
      </td>
    );
  }

  const isPct = column.kind === "deltaPct";
  const alert = isPct && isAlert(value);
  const immaterial = isPct && !alert && isImmaterial(value);

  return (
    <td
      className={`${density.cell} whitespace-nowrap border-b border-gray-100 text-right font-semibold tabular-nums ${deltaTone(value)} ${divider} ${
        isPct ? deltaTint(value) : ""
      }`}
      title={
        !isPct
          ? formatExact(value, column.unit)
          : immaterial
            ? `${formatDeltaPct(value)} — within ±${MATERIAL_THRESHOLD}%, on plan`
            : alert
              ? `${formatDeltaPct(value)} — at or past the ${ALERT_THRESHOLD}% attention threshold`
              : undefined
      }
    >
      <span className="inline-flex items-center justify-end gap-1.5">
        {/* The marker slot is reserved on every percentage cell, marked or not, so the
            figures stay in one column instead of jogging left and right down the grid. */}
        {isPct && (
          <span className="inline-flex h-3 w-3 flex-shrink-0 items-center justify-center">
            {alert && (
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="h-3 w-3"
                aria-hidden="true"
              />
            )}
          </span>
        )}
        {immaterial ? (
          <span className="sr-only">within {MATERIAL_THRESHOLD} percent, on plan</span>
        ) : isPct ? (
          formatDeltaPct(value)
        ) : (
          formatDelta(value, column.unit)
        )}
        {alert && <span className="sr-only"> — needs attention</span>}
      </span>
    </td>
  );
}

function ToggleChip({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#144677]/40 disabled:cursor-not-allowed disabled:opacity-50 ${
        active
          ? "border-[#144677] bg-[#144677] text-white"
          : "cursor-pointer border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-[#144677]"
      }`}
    >
      {children}
    </button>
  );
}

export default function PerformanceTable({
  rows,
  totals,
  metrics,
  periodShort,
  periodLabel,
}) {
  const [visibleGroups, setVisibleGroups] = useState(
    () => new Set(COLUMN_GROUPS.map((group) => group.key)),
  );
  const [sort, setSort] = useState({ columnId: null, direction: "asc" });
  const [search, setSearch] = useState("");

  const density = DENSITY;
  const searching = search.trim().length > 0;

  const columns = useMemo(
    () => buildColumns(metrics, periodShort, visibleGroups),
    [metrics, periodShort, visibleGroups],
  );
  const headerGroups = useMemo(
    () => buildHeaderGroups(columns, metrics),
    [columns, metrics],
  );

  /** First column of each metric block. The divider hangs off these so the rule runs
   *  the full height of the grid rather than stopping at the header. */
  const { sectionStartIds, sectionIndexById } = useMemo(() => {
    const ids = new Set();
    const indexById = new Map();
    let block = -1;
    columns.forEach((column, index) => {
      const isNewBlock = index === 0 || column.metricKey !== columns[index - 1].metricKey;
      if (isNewBlock) {
        block += 1;
        if (index > 0) ids.add(column.id);
      }
      indexById.set(column.id, block);
    });
    return { sectionStartIds: ids, sectionIndexById: indexById };
  }, [columns]);

  const availableGroups = useMemo(() => {
    const available = new Set();
    (metrics ?? []).forEach((metric) =>
      (metric.variants ?? []).forEach((variant) => available.add(variant)),
    );
    return available;
  }, [metrics]);

  // Search first, then sort what survived.
  const visibleRows = useMemo(() => {
    const matched = filterTree(rows ?? [], search);
    const sortColumn = columns.find((column) => column.id === sort.columnId) ?? null;
    return sortTree(matched, sortColumn, sort.direction);
  }, [rows, search, columns, sort]);

  const toggleGroup = (key) => {
    setVisibleGroups((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // A hidden column cannot remain the sort key, or the order becomes unexplainable.
    setSort((previous) =>
      columns.find((c) => c.id === previous.columnId)?.group === key
        ? { columnId: null, direction: "asc" }
        : previous,
    );
  };

  const requestSort = (columnId) =>
    setSort((previous) =>
      previous.columnId === columnId
        ? { columnId, direction: previous.direction === "desc" ? "asc" : "desc" }
        : // Numeric columns open descending: the biggest shortfall or the largest
          // account is almost always what the reader wants first.
          { columnId, direction: "desc" },
    );

  const exportCsv = () => {
    const slug = (periodLabel || periodShort || "report")
      .replace(/[^\w-]+/g, "-")
      .toLowerCase();
    downloadCsv(toCsv(visibleRows, columns), `performance-report-${slug}.csv`);
  };

  const headText = "text-[11px] font-semibold uppercase tracking-wider text-gray-500";

  if (!metrics?.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
        <p className="text-sm text-gray-500">No metrics are available for this period.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search billing company or code…"
            aria-label="Search billing companies"
            className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 transition-colors duration-200 placeholder:text-gray-400 focus:border-[#144677]/40 focus:outline-none focus:ring-2 focus:ring-[#144677]/15"
          />
        </div>

        <div className="flex items-center gap-1.5" role="group" aria-label="Column groups">
          <span className={headText}>Compare</span>
          {COLUMN_GROUPS.map((group) => (
            <ToggleChip
              key={group.key}
              active={visibleGroups.has(group.key)}
              disabled={group.locked || !availableGroups.has(group.key)}
              onClick={() => toggleGroup(group.key)}
            >
              {group.label}
            </ToggleChip>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={exportCsv}
            className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg bg-[#144677] px-3 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#1a5691] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#144677]/40"
          >
            <FontAwesomeIcon icon={faFileCsv} className="h-3 w-3" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Grid. Both scroll axes live in this one container so the header row and the
          identity columns can stay pinned to it. */}
      <div className="max-h-[68vh] overflow-auto rounded-b-2xl">
        <table className="min-w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th
                rowSpan={2}
                scope="col"
                style={{ left: 0, width: NAME_WIDTH, minWidth: NAME_WIDTH }}
                className={`sticky left-0 top-0 z-30 border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left ${headText}`}
              >
                Billing Company
              </th>
              {/* Alternating tint plus the section rule: the band tells you where a
                  metric block starts even when its label has scrolled out of reach. */}
              {headerGroups.map((group, index) => (
                <th
                  key={group.metricKey}
                  colSpan={group.span}
                  scope="colgroup"
                  className={`sticky top-0 z-20 h-9 border-b border-gray-200 px-3 text-center text-xs font-bold uppercase tracking-wider text-[#144677] ${
                    SECTION_TINTS[index % SECTION_TINTS.length]
                  } ${index > 0 ? SECTION_DIVIDER : ""}`}
                >
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((column) => {
                const isSorted = sort.columnId === column.id;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={
                      isSorted
                        ? sort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className={`sticky top-9 z-20 border-b border-gray-200 p-0 ${
                      SECTION_TINTS[
                        (sectionIndexById.get(column.id) ?? 0) % SECTION_TINTS.length
                      ]
                    } ${sectionStartIds.has(column.id) ? SECTION_DIVIDER : ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => requestSort(column.id)}
                      title={`Sort by ${column.label}`}
                      className={`flex w-full cursor-pointer items-center justify-end gap-1 whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200 hover:text-[#144677] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#144677]/40 ${
                        isSorted ? "text-[#144677]" : "text-gray-500"
                      }`}
                    >
                      {column.label}
                      <FontAwesomeIcon
                        icon={
                          isSorted
                            ? sort.direction === "asc"
                              ? faArrowUp
                              : faArrowDown
                            : faSort
                        }
                        className={`h-2.5 w-2.5 flex-shrink-0 ${isSorted ? "opacity-100" : "opacity-30"}`}
                      />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.row_id}
                className="bg-white transition-colors duration-150 hover:bg-blue-50/40"
              >
                <th
                  scope="row"
                  style={{ left: 0, width: NAME_WIDTH, minWidth: NAME_WIDTH }}
                  className={`sticky left-0 z-10 border-b border-r border-gray-100 bg-white text-left ${density.name}`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span
                      className="truncate font-medium text-gray-800"
                      title={row.billing_code ? `${row.name} (${row.billing_code})` : row.name}
                    >
                      {row.name}
                      {row.billing_code && (
                        <span className="ml-1 font-mono text-[11px] font-normal text-gray-400">
                          ({row.billing_code})
                        </span>
                      )}
                    </span>
                    {row.parent_company && row.parent_company !== row.name && (
                      <span
                        className="truncate text-[11px] font-normal text-gray-400"
                        title={`Parent: ${row.parent_company}`}
                      >
                        {row.parent_company}
                      </span>
                    )}
                  </span>
                </th>
                {columns.map((column) => (
                  <Cell
                    key={column.id}
                    row={row}
                    column={column}
                    density={density}
                    isSectionStart={sectionStartIds.has(column.id)}
                  />
                ))}
              </tr>
            ))}

            {!visibleRows.length && (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-12 text-center">
                  <p className="text-sm font-medium text-gray-500">
                    No billing companies match this view
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {searching ? "Try a different search term." : "Adjust the filters above."}
                  </p>
                </td>
              </tr>
            )}
          </tbody>

          {/* Totals stay pinned to the bottom: a row is usually being read against the
              whole book of business. Every billing code appears exactly once, so this
              cannot double count. */}
          {totals && visibleRows.length > 0 && (
            <tfoot>
              <tr>
                <th
                  scope="row"
                  style={{ left: 0, width: NAME_WIDTH, minWidth: NAME_WIDTH }}
                  className={`sticky bottom-0 left-0 z-30 border-r border-t-2 border-gray-300 bg-[#eef4fa] text-left text-xs font-bold uppercase tracking-wider text-[#144677] ${density.name}`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span>Total</span>
                    <span className="text-[11px] font-medium normal-case tracking-normal text-gray-500">
                      all billing companies
                    </span>
                  </span>
                </th>
                {columns.map((column) => {
                  // Percentages are dbt-computed per row and are not summable, so the
                  // totals row leaves them blank rather than showing a figure this
                  // application invented.
                  if (!totalIsAvailable(totals, column)) {
                    return (
                      <td
                        key={column.id}
                        className={`sticky bottom-0 z-20 border-t-2 border-gray-300 bg-[#eef4fa] text-right text-gray-300 ${density.cell} ${
                          sectionStartIds.has(column.id) ? SECTION_DIVIDER : ""
                        }`}
                        title="Not totalled — calculated per row in dbt from period totals"
                      >
                        ·
                      </td>
                    );
                  }
                  const value = readCell({ metrics: totals }, column);
                  return (
                    <td
                      key={column.id}
                      className={`sticky bottom-0 z-20 whitespace-nowrap border-t-2 border-gray-300 bg-[#eef4fa] text-right font-bold tabular-nums ${density.cell} ${
                        sectionStartIds.has(column.id) ? SECTION_DIVIDER : ""
                      } ${column.kind === "value" ? "text-[#144677]" : deltaTone(value)}`}
                      title={formatExact(value, column.unit)}
                    >
                      {column.kind === "value"
                        ? formatValue(value, column.unit)
                        : formatDelta(value, column.unit)}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-2.5">
        <p className="text-xs text-gray-400">
          {visibleRows.length} of {rows?.length ?? 0} billing companies
          {searching && " · filtered"}
          {/* Both are scope, not formatting, so they sit with the count rather than in
              the symbol legend opposite. */}
          <span className="ml-1.5 text-gray-300">
            · listed once an actual or a budget exists · GP % is dbt-calculated per row
          </span>
        </p>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-400">
          <span className="inline-flex items-center gap-1">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="h-2.5 w-2.5 text-red-500"
              aria-hidden="true"
            />
            {ALERT_THRESHOLD}% or more below
          </span>
          <span>blank % = within ±{MATERIAL_THRESHOLD}%</span>
          <span>
            <span className="font-medium">·</span> = not totalled
          </span>
          <span>— = no data</span>
        </p>
      </div>
    </div>
  );
}
